// Баннер «Нет связи» (задача D5, ui-spec §3.10): последний запрос до сервиса Анны не дошёл, а данные на экране есть —
// «Нет связи — данные на 14:32 МСК» и «Повторить» (перезапрос открытых данных). Данных ещё не было — баннера нет:
// экран сам скажет «Сервис Анны не отвечает». Первый же удачный ответ убирает баннер (`markOk`, D4).
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, WifiOff } from 'lucide-react';
import { useState } from 'react';
import { useConnection, type ConnectionState } from '@/api/connection';
import { Button } from '@/components/ui/button';
import { formatTime } from '@/lib/format';
import { cn } from '@/lib/utils';

/** Текст баннера по моменту последнего ответа. */
export function offlineTextRu(lastOkAt: number): string {
  return `Нет связи — данные на ${formatTime(new Date(lastOkAt))} МСК`;
}

/** Баннер на экране: последний запрос до сервиса не дошёл, а данные уже были. Экраны по нему не дублируют «Повторить». */
export function isOfflineBannerShown(c: ConnectionState): c is ConnectionState & { readonly lastOkAt: number } {
  return c.state === 'offline' && c.lastOkAt !== null;
}

export function OfflineBanner() {
  const connection = useConnection();
  const queryClient = useQueryClient();
  const [retrying, setRetrying] = useState(false);
  if (!isOfflineBannerShown(connection)) return null;

  return (
    // <output> — роль status: чтение с экрана скажет о баннере вежливо, не перебивая.
    <output className="flex items-center gap-2 border-b border-border bg-surface-2 py-1.5 pr-3 pl-6 text-sm max-lg:pl-4">
      <WifiOff aria-hidden="true" className="size-3.5 shrink-0 text-st-error-dot" />
      <span className="min-w-0 flex-1">{offlineTextRu(connection.lastOkAt)}</span>
      <Button
        variant="outline"
        size="sm"
        className="max-sm:h-10"
        disabled={retrying}
        onClick={() => {
          setRetrying(true);
          void queryClient.refetchQueries({ type: 'active' }).finally(() => {
            setRetrying(false);
          });
        }}
      >
        <RefreshCw aria-hidden="true" className={cn(retrying && 'spin')} />
        Повторить
      </Button>
    </output>
  );
}
